import { invoke, get } from 'lodash';
import { appLog } from 'utils/helpers';
import { getClientFrame } from 'admin/utils/get-client-frame';
import {
	CardData,
	EditableStateInfo,
	SelectedBB,
	StorySettingsType,
	StorySymbols,
	StoryVersionType,
} from 'types/story';
import { AdminReducerState } from 'admin/reducers';

const IFRAME_ACTIONS = {
	GET_STORY: '@IFRAME/GET_STORY',
	UPDATE_ADMIN_PREVIEW_LOCATION: '@IFRAME/UPDATE_ADMIN_LOCATION',
	UPDATE_CARD_ELEMENT_BY_PATH: '@IFRAME/UPDATE_CARD_ELEMENT_BY_PATH',
	UPDATE_STORY_ELEMENT_BY_PATH: '@IFRAME/UPDATE_STORY_ELEMENT_BY_PATH',
	SET_EDITABLE_EDITOR_ELEMENT: '@IFRAME/SET_EDITABLE_EDITOR_ELEMENT',
	INITIATE_SELECT_EDITOR_ELEMENT: '@IFRAME/INITIATE_SELECT_EDITOR_ELEMENT',
	FORCE_SELECTION_HINT_UPDATE: '@IFRAME/FORCE_SELECTION_HINT_UPDATE',
	APPLY_SCROLLBAR_FIX: '@IFRAME/APPLY_SCROLLBAR_FIX',
	UPLOAD_IMAGE: '@IFRAME/UPLOAD_IMAGE',
	EDIT_IMAGE: '@IFRAME/EDIT_IMAGE',
	UPDATE_CLIENT_CARD_DATA: '@EDITOR/UPDATE_CLIENT_CARD_DATA',
	UPDATE_CLIENT_STORY_ELEMENTS: '@EDITOR/UPDATE_CLIENT_STORY_ELEMENTS',
} as const;

type MessageToAdmin = { target: typeof IframeTunnel.targets.admin } & MessageDataToAdmin;

type MessageToClient = { target: typeof IframeTunnel.targets.client } & MessageDataToClient;

type ReceiverMessageEvent = MessageEvent<
	{ storycardsIframeMessage: boolean } & (MessageDataToAdmin | MessageDataToClient)
>;

type Props = {
	messageReceiver?: (event: ReceiverMessageEvent) => void;
	id: string; // invoker id (used for logs only)
	filter?: string[]; // filter messages by action type
};

class IframeTunnel {
	static targets = {
		admin: 'admin', // transmit to admin
		client: 'client', // transmit to client
	} as const;

	messageReceiver: Props['messageReceiver'];

	id: Props['id'];

	filter?: Props['filter'];

	sourceName: 'CLIENT' | 'ADMIN';

	log: (...args: any[]) => void;

	constructor(props: Props = { id: 'unknown' }) {
		this.sourceName = window.location.pathname.match(/preview.html/g) ? 'CLIENT' : 'ADMIN';
		this.id = `${this.sourceName}:${props.id}`;
		this.filter = props.filter;

		this.log = appLog.extend('IframeTunnel');

		this.messageReceiver = props.messageReceiver;
	}

	_messageReceiver = (event: ReceiverMessageEvent) => {
		const isSecureOrigin = window.location.origin === event.origin;
		const isSecureMessage = event.data.storycardsIframeMessage;

		if (!this.messageReceiver || !isSecureMessage || !isSecureOrigin) {
			return;
		}

		if (this.filter && !this.filter.includes(event.data.action)) {
			return;
		}

		this.log(`%c[GET]  ${this.id} »%c${event.data.action} %O`, 'font-weight: bold;', 'color: #66CDAA;', event);

		this.messageReceiver(event);
	};

	init() {
		this.log(`%c[INIT] ${this.id}`, 'font-weight: bold;');
		window.addEventListener('message', this._messageReceiver);
	}

	destroy() {
		this.log(`%c[DESTROY] ${this.id}`, 'font-weight: bold;');
		window.removeEventListener('message', this._messageReceiver);
	}

	/**
	 * @param props
	 * @param props.action {String}
	 * @param props.payload {Object}
	 * @params props.target {String} OneOf(['client', 'admin']) To where transmit the message
	 */

	messageTransmitter = (props: MessageToClient | MessageToAdmin) => {
		this.log(`%c[POST] ${this.id} » %c${props.action} %O`, 'font-weight: bold;', 'color: #66CDAA;', props);
		const targets = {
			[IframeTunnel.targets.client]: () => getClientFrame().contentWindow,
			[IframeTunnel.targets.admin]: () => window.parent,
		};

		const targetWindow = invoke(targets, get(props, 'target', IframeTunnel.targets.admin));

		if (!targetWindow) {
			this.log(`%c[POST] ${this.id} » targetWindow is NOT FOUND`, 'font-weight: bold;');
			return;
		}

		targetWindow.postMessage(
			{
				storycardsIframeMessage: true, // key used to drop 3rd party libraries messages
				action: get(props, 'action'),
				payload: get(props, 'payload'),
			},
			window.location.origin
		);
	};
}

/**
 * postMessage utility function
 */
function transmitTo(props: { id: string } & (MessageToClient | MessageToAdmin)) {
	const { id, target, action, payload } = props;
	const iframeTunnel = new IframeTunnel({ id });
	iframeTunnel.messageTransmitter({ target, action, payload } as MessageToClient | MessageToAdmin);
}

type MessageDataToAdmin =
	| {
			action: typeof IFRAME_ACTIONS.APPLY_SCROLLBAR_FIX;
			payload: { frameSelector: string; frameWidth: string };
	  }
	| {
			action: typeof IFRAME_ACTIONS.SET_EDITABLE_EDITOR_ELEMENT;
			payload: SelectedBB;
	  }
	| {
			action: typeof IFRAME_ACTIONS.UPDATE_ADMIN_PREVIEW_LOCATION;
			payload: Record<string, string>;
	  }
	| {
			action:
				| typeof IFRAME_ACTIONS.UPDATE_CARD_ELEMENT_BY_PATH
				| typeof IFRAME_ACTIONS.UPDATE_STORY_ELEMENT_BY_PATH;
			payload: { path: string | string[]; value: string | string[] };
	  }
	| {
			action: typeof IFRAME_ACTIONS.GET_STORY;
			payload?: never;
	  }
	| {
			action: typeof IFRAME_ACTIONS.UPLOAD_IMAGE;
			payload?: never;
	  }
	| {
			action: typeof IFRAME_ACTIONS.EDIT_IMAGE;
			payload: boolean;
	  };

type MessageDataToClient =
	| {
			action: typeof IFRAME_ACTIONS.INITIATE_SELECT_EDITOR_ELEMENT;
			payload: {
				editableState: EditableStateInfo;
				id: string;
				properties?: {
					bubbles?: boolean;
					shiftKey?: boolean;
				};
			};
	  }
	| {
			action: typeof IFRAME_ACTIONS.UPDATE_CLIENT_CARD_DATA;
			payload: {
				editableState: EditableStateInfo;
				data?: CardData;
				settings?: StorySettingsType;
				symbols?: StorySymbols;
			};
	  }
	| {
			action: typeof IFRAME_ACTIONS.UPDATE_CLIENT_STORY_ELEMENTS;
			payload: {
				editableState: EditableStateInfo;
				elements?: StoryVersionType['data']['elements'];
				settings?: StorySettingsType;
				symbols?: StorySymbols;
			};
	  }
	| {
			action: typeof IFRAME_ACTIONS.GET_STORY;
			payload: Required<AdminReducerState['storyEditor']>;
	  }
	| { action: typeof IFRAME_ACTIONS.FORCE_SELECTION_HINT_UPDATE; payload: undefined };

export { IframeTunnel, transmitTo, IFRAME_ACTIONS, MessageDataToAdmin, MessageDataToClient };
